/**
 * MyAssessmentsPage — agent-facing list of online assessments with window
 * states (upcoming/available/completed/missed) and Start/Retake actions.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarClock, CheckCircle2, XCircle, Play, AlertTriangle } from 'lucide-react';
import { useApi } from '../../hooks/useApi';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';

const STATE_BADGE = {
  upcoming: { label: 'Upcoming', cls: 'bg-slate-100 text-slate-700' },
  available: { label: 'Available Now', cls: 'bg-emerald-100 text-emerald-800' },
  retake_available: { label: 'Retake Available', cls: 'bg-warm-200 text-warm-800' },
  in_progress: { label: 'In Progress', cls: 'bg-amber-100 text-amber-800' },
  completed: { label: 'Completed', cls: 'bg-emerald-100 text-emerald-800' },
  missed: { label: 'Missed', cls: 'bg-rose-100 text-rose-800' },
};

export default function MyAssessmentsPage() {
  const { get } = useApi();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    get('/me/assessments')
      .then(r => { if (!cancelled) setRows(r.assessments || []); })
      .catch(e => { if (!cancelled) setLoadError(e.response?.data?.detail || 'Could not load your assessments. Please try again.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [get]);

  if (loading) return <p className="text-sm text-muted-foreground">Loading assessments…</p>;

  if (loadError) return (
    <div className="space-y-6" data-testid="my-assessments-page">
      <h1 className="font-display text-3xl font-bold tracking-tight">My Assessments</h1>
      <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 flex items-start gap-3" data-testid="my-assessments-error">
        <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold text-sm text-amber-900">Couldn't load your assessments</p>
          <p className="text-sm text-amber-800 mt-0.5">{loadError}</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="my-assessments-page">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight">My Assessments</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Scheduled tests from your trainer. Timed — make sure you're ready before starting.
        </p>
      </div>

      {rows.length === 0 ? (
        <Card className="p-12 text-center">
          <CalendarClock className="w-12 h-12 mx-auto text-muted-foreground/40 mb-3" />
          <p className="font-display text-lg font-semibold">No assessments yet</p>
          <p className="text-sm text-muted-foreground mt-1">
            When your trainer schedules an assessment you'll see it here (and get an email).
          </p>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="my-assessment-list">
          {rows.map(a => {
            const badge = STATE_BADGE[a.state] || STATE_BADGE.upcoming;
            const canStart = ['available', 'retake_available', 'in_progress'].includes(a.state);
            return (
              <Card key={a.assessment_id} className="p-4" data-testid={`my-assessment-${a.assessment_id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-display font-semibold">{a.name}</h3>
                      <Badge className={`${badge.cls} border-0`}>{badge.label}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(a.start_at).toLocaleString()} → {new Date(a.end_at).toLocaleString()}
                      {' '}· {a.duration_minutes} min · Pass {a.passing_pct}%
                      {' '}· Attempts {a.attempts_used}/{a.attempts_allowed}
                    </p>
                    {a.best_score != null && (
                      <p className="text-sm mt-1 inline-flex items-center gap-1">
                        {a.best_score >= a.passing_pct
                          ? <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                          : <XCircle className="w-4 h-4 text-rose-600" />}
                        Best score: <strong>{a.best_score}%</strong>
                      </p>
                    )}
                  </div>
                  {canStart && (
                    <Button onClick={() => navigate(`/my-assessments/${a.assessment_id}/take`)}
                            data-testid={`start-assessment-${a.assessment_id}`}>
                      <Play className="w-4 h-4 mr-1" />
                      {a.state === 'in_progress' ? 'Resume' : a.state === 'retake_available' ? 'Retake' : 'Start Assessment'}
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
